import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useResourceBookingSession from 'features/Reserve/hooks/useResourceBookingSession';
import { CollectionPageSkeleton } from 'features/Reserve/pages/Skeleton';
import { ANALYTICS_EVENT, ANALYTICS_PROPERTY } from '@kouto/types';
import { getProductFromResourceGroups } from 'features/analytics/services/analytics.helpers';
import { usePageViewEvent } from 'hooks/usePageViewEvent';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import { currencyFormat } from 'utils';
import getFirstAvailableDateFromSessions from 'utils/data/firstAvailableDate/getFirstAvailableDateFromSessions';
import { NonMappedCollectionPage } from './NonMapped';
import { MappedCollectionPage } from './Mapped';

export const CollectionPage = () => {
  const { t: translate } = useTranslation();
  const { listing, isLoadingListing, sessions } = useResourceBookingSession();
  const currency = useBrandCurrency();
  const isMapped = listing?.isMapped ?? false;

  const analyticsItems = useMemo(() => {
    const items = getProductFromResourceGroups({
      groups: listing?.resourceGroups ?? [],
      categoryName: listing?.category?.name,
    });

    const data = items.reduce(
      (acc, item) => {
        acc.ids.push(item.id ?? '');
        acc.titles.push(item.title ?? '');

        acc.categories.push(item.category ?? '');
        return acc;
      },
      {
        ids: [] as string[],
        titles: [] as string[],
        categories: [] as string[],
      },
    );

    return { items, data };
  }, [listing?.resourceGroups, listing?.category?.name]);

  usePageViewEvent({
    eventName: ANALYTICS_EVENT.VIEW_COLLECTION,
    payload: {
      [ANALYTICS_PROPERTY.AllExperiences]:
        analyticsItems.data.titles.join(';') || '',
      [ANALYTICS_PROPERTY.Products]: JSON.stringify(analyticsItems.items),
      [ANALYTICS_PROPERTY.ExperienceTitle]:
        listing?.title ?? 'Title unavailable',
      [ANALYTICS_PROPERTY.ExperienceDate]:
        getFirstAvailableDateFromSessions(sessions)?.startDateTime.split(
          'T',
        )[0],
      [ANALYTICS_PROPERTY.ExperienceLocation]: listing?.location?.name,
      [ANALYTICS_PROPERTY.ExperienceProduct]: listing?.productLine,
      [ANALYTICS_PROPERTY.ExperiencePrice]: currencyFormat(currency)(
        listing?.startingPrice ?? 0,
      ),
    },
    optionalPayload: {
      [ANALYTICS_PROPERTY.ExperienceCount]:
        analyticsItems.items?.length?.toString(),
      [ANALYTICS_PROPERTY.AllCategories]:
        analyticsItems.data.categories.join(';') || '',
      [ANALYTICS_PROPERTY.ListId]: listing?.id,
      [ANALYTICS_PROPERTY.ListName]: listing?.title ?? 'Title unavailable',
    },
    isNotReady: isLoadingListing || !listing || !currency,
  });

  if (isLoadingListing && !listing) {
    return <CollectionPageSkeleton />;
  }

  if (!listing) {
    return <h1>{translate('noListingFound')}</h1>;
  }

  if (isMapped) return <MappedCollectionPage />;

  return <NonMappedCollectionPage />;
};
